import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Typography, Grid, Box, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Backdrop from "@mui/material/Backdrop";
import Button from '@mui/material/Button';
import { Blocks } from "react-loader-spinner";
import FinancialHandler from "../../handlers/FinancialHandler";
import excelImg from './images/excel.png';
import quickbooksImg from './images/quickbooks.png';
import xeroImg from './images/xero.png';
import freshbooksImg from './images/freshbooks.png';
import waveImg from './images/wave.png';
import ShareLinkModal from './ShareLinkModal';

const environment = process.env.REACT_APP_ENVIRONMENT;

// Custom styles using Material-UI's makeStyles
const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(4, 0),
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
  },
  paper: {
    padding: theme.spacing(2),
    height: '100%',
  },
}));

const dataSources = [
  {
    name: 'Excel',
    subtitle: 'Import from Excel Model',
    description:
      'Importing financial data from Excel spreadsheets allows users to seamlessly integrate their existing financial records into our system. This feature supports a variety of Excel formats, making it easy for users to upload their financial statements without the need for complex data migration processes.',
    image: excelImg,
  },
  {
    name: 'QuickBooks',
    subtitle: environment === 'sandbox' ? 'About QuickBooks Sandbox' : 'About QuickBooks',
    description: environment === 'sandbox' ?
      'The Quickbooks Sandbox is only used for the testing server. You have to use Intuit developer account sandbox companies for the testing server.'
      :
      'QuickBooks is a leading cloud accounting software system developed by Intuit. With millions of users worldwide, they provide users and customers with software to run their small business accounting. Create invoices or pay bills without worrying about manual reconciliation. Use our QuickBooks Online integration, fully automate tasks, and spend less time in your accounting software.',
    image: quickbooksImg,
  },
  // {
  //   name: 'Xero',
  //   subtitle: 'About Xero',
  //   description:
  //     'Xero is a cloud-based accounting software platform for small and medium-sized businesses. It offers features like invoicing, bank reconciliation, inventory, purchasing, expenses, and fixed asset depreciation. With its user-friendly interface and robust integrations, Xero helps businesses manage their finances efficiently.',
  //   image: xeroImg,
  // },
  // {
  //   name: 'FreshBooks',
  //   subtitle: 'About FreshBooks',
  //   description:
  //     'FreshBooks is an intuitive cloud-based accounting software designed for small businesses. It offers features such as invoicing, expense tracking, time tracking, and financial reporting. FreshBooks helps businesses streamline their accounting processes and improve client relationships.',
  //   image: freshbooksImg,
  // },
  // {
  //   name: 'Wave',
  //   subtitle: 'About Wave',
  //   description:
  //     'Wave is a free, cloud-based accounting software designed for small businesses and freelancers. It offers features like invoicing, accounting, receipt scanning, and personal finance management. With its cost-effective solutions, Wave helps businesses manage their finances without breaking the bank.',
  //   image: waveImg,
  // },
];

const DataSourceSelection = () => {
  const classes = useStyles();
  const location = useLocation();
  const [modalVisibility, setModalVisibility] = useState(false);
  const [companyId, setCompanyId] = useState("NULL");
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('user');
  const companyName = queryParams.get('name');

  const getCompanyId = async () => {
    const companyId = await FinancialHandler.fetchCompanyId({ userId, companyName });
    setCompanyId(companyId);
    // const authUri = window.location.href;
    // window.navigator.clipboard.writeText(authUri);
    // companyId !== "" && Swal.fire("Link for Share", authUri, "info");
  };

  const handleClick = async (source) => {
    let authUri;
    if(source === "Excel") {
      authUri = window.location.href;
      authUri = authUri.replace("source-select", "excel-import");   //get the excel-import page uri
    }
    else if(source === "QuickBooks") {
      authUri = await FinancialHandler.fetchQBOAuthUri({ environment, companyId });
    }
    window.location.href = authUri;
  };

  useEffect(() => {
    getCompanyId();
  }, []);

  return (
    <Container maxWidth="md" className="py-8">
      {companyId === "NULL" ?
        <Backdrop
          sx={{ color: "fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={companyId === "NULL"}
        >
          <Blocks visible={true} height="80" width="80" />
        </Backdrop>
      : 
        (
          companyId === "" ?
            <Typography variant="h4" className="text-center text-indigo-600 font-bold">
              This link is invalid
            </Typography>
          :
            <>
              {/* Modal */}
              <ShareLinkModal open={modalVisibility} onClose={() => setModalVisibility(false)} />

              {/* Title */}
              <Typography variant="h4" className="text-center text-indigo-600 font-bold">
                Please Select a Data Source
              </Typography>

              <div className="py-7 pr-10 flex justify-end">
                <Button onClick={() => setModalVisibility(true)} color="primary" variant="contained">
                  Share this page
                </Button>
              </div>

              {/* Data Sources List */}
              <Grid container spacing={6}>
                {dataSources.map((source, index) => {
                  const isEven = index % 2 === 0;
                  return (
                    <Grid item xs={12} key={source.name}>
                      <button onClick={() => handleClick(source.name)}>
                        <Paper elevation={3} className={classes.paper}>
                          <Grid
                            container
                            spacing={4}
                            direction={isEven ? 'row' : 'row-reverse'}
                            alignItems="center"
                          >
                            {/* Image Section */}
                            <Grid item xs={12} md={6}>
                              <img
                                src={source.image}
                                alt={source.name}
                                className={`${classes.image} shadow-lg`}
                              />
                            </Grid>

                            {/* Text Section */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="h5"
                                component="h2"
                                className="font-semibold mb-2"
                              >
                                {source.subtitle}
                              </Typography>
                              <Typography variant="body1" className="text-gray-700">
                                {source.description}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </button>
                    </Grid>
                  );
                })}
              </Grid>
            </>
        )
      }
    </Container>
  );
};

export default DataSourceSelection;